// Conception — types, libellés et règles d'affichage côté interface.
// Les règles de compatibilité font foi côté serveur ; elles sont reproduites ici pour expliquer l'état à l'écran avant l'enregistrement.

export type TypeNoeud = 'automate' | 'controleur' | 'passerelle' | 'superviseur' | 'alimentation_bus' | 'alimentation' | 'capteur'
  | 'actionneur' | 'compteur' | 'variateur' | 'commutateur_reseau' | 'poste_commande' | 'equipement_existant' | 'autre';
export type TypeLiaison = 'knx' | 'bacnet_ip' | 'bacnet_mstp' | 'modbus_tcp' | 'modbus_rtu' | 'dali' | 'mbus' | 'enocean' | 'ip'
  | 'alimentation' | 'tor' | 'analogique' | 'autre';
export type Compatibilite = 'etayee' | 'a_verifier';

export interface Noeud {
  id: string; type: TypeNoeud; label: string; fabricant?: string | null; reference?: string | null; zone?: string | null;
  protocoles: string[]; x: number; y: number; note?: string | null;
}
export interface SourceLiaison { versionId: string; page: number }
export interface Liaison {
  id: string; de: string; vers: string; type: TypeLiaison; libelle?: string | null; compatibilite: Compatibilite; source?: SourceLiaison | null;
}
export interface Donnees { nodes: Noeud[]; links: Liaison[] }

export interface DescriptionSource { titre: string; version: string; pages: number | null; statut: string; portee: 'bibliotheque' | 'dossier' }
export interface SchemaVue {
  id: string; dossierId: string; nom: string; data: Donnees; revision: number; misAJourLe: string; misAJourPar: string | null;
  sources: Record<string, DescriptionSource>;
  corrections?: { liaison: string; motif: string; message: string }[];
}

export const TYPES_NOEUD: { id: TypeNoeud; libelle: string }[] = [
  { id: 'automate', libelle: 'Automate' }, { id: 'controleur', libelle: 'Contrôleur' }, { id: 'passerelle', libelle: 'Passerelle' },
  { id: 'superviseur', libelle: 'Superviseur' }, { id: 'alimentation_bus', libelle: 'Alimentation de bus' },
  { id: 'alimentation', libelle: 'Alimentation' }, { id: 'capteur', libelle: 'Capteur' }, { id: 'actionneur', libelle: 'Actionneur' },
  { id: 'compteur', libelle: 'Compteur' }, { id: 'variateur', libelle: 'Variateur' }, { id: 'commutateur_reseau', libelle: 'Commutateur réseau' },
  { id: 'poste_commande', libelle: 'Poste de commande' }, { id: 'equipement_existant', libelle: 'Équipement existant' }, { id: 'autre', libelle: 'Autre' },
];
export const LIBELLE_NOEUD = Object.fromEntries(TYPES_NOEUD.map((t) => [t.id, t.libelle])) as Record<TypeNoeud, string>;

export const TYPES_LIAISON: { id: TypeLiaison; libelle: string; court: string; detail?: string }[] = [
  { id: 'knx', libelle: 'KNX', court: 'KNX' },
  { id: 'bacnet_ip', libelle: 'BACnet/IP', court: 'BACnet/IP' },
  { id: 'bacnet_mstp', libelle: 'BACnet MS/TP', court: 'MS/TP' },
  { id: 'modbus_tcp', libelle: 'Modbus TCP', court: 'Modbus TCP' },
  { id: 'modbus_rtu', libelle: 'Modbus RTU', court: 'Modbus RTU' },
  { id: 'dali', libelle: 'DALI', court: 'DALI' },
  { id: 'mbus', libelle: 'M-Bus', court: 'M-Bus' },
  { id: 'enocean', libelle: 'EnOcean', court: 'EnOcean' },
  { id: 'ip', libelle: 'Réseau IP', court: 'IP' },
  { id: 'alimentation', libelle: 'Alimentation', court: 'Alim.' },
  { id: 'tor', libelle: 'Tout ou rien', court: 'TOR', detail: 'contact sec, signal on/off' },
  { id: 'analogique', libelle: 'Analogique', court: 'Ana.', detail: '0-10 V, 4-20 mA' },
  { id: 'autre', libelle: 'Autre', court: 'Autre' },
];
export const LIBELLE_LIAISON = Object.fromEntries(TYPES_LIAISON.map((t) => [t.id, t.libelle])) as Record<TypeLiaison, string>;
export const COURT_LIAISON = Object.fromEntries(TYPES_LIAISON.map((t) => [t.id, t.court])) as Record<TypeLiaison, string>;

/** Protocoles proposés sur un équipement (mêmes écritures que les types de liaison, reconnues par le serveur). */
export const PROTOCOLES_PROPOSES = ['KNX', 'BACnet/IP', 'BACnet MS/TP', 'Modbus TCP', 'Modbus RTU', 'DALI', 'M-Bus', 'EnOcean', 'Réseau IP'];

const SANS_PROTOCOLE: TypeLiaison[] = ['alimentation', 'tor', 'analogique'];
const ALIAS: Record<TypeLiaison, string[]> = {
  knx: ['knx', 'knxtp', 'knxip'], bacnet_ip: ['bacnetip'], bacnet_mstp: ['bacnetmstp'], modbus_tcp: ['modbustcp'], modbus_rtu: ['modbusrtu'],
  dali: ['dali', 'dali2'], mbus: ['mbus'], enocean: ['enocean'], ip: ['ip', 'reseauip', 'ethernet', 'tcpip'],
  alimentation: [], tor: [], analogique: [], autre: ['autre'],
};
export const normaliserProtocole = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]/g, '');
export const declare = (protocoles: string[], type: TypeLiaison) => protocoles.some((p) => ALIAS[type].includes(normaliserProtocole(p)));

/** État de compatibilité réellement retenu et raison, pour l'affichage. */
export function compatibiliteEffective(l: Liaison, nodes: Noeud[]): { etat: Compatibilite; raison: string | null } {
  if (l.compatibilite !== 'etayee') return { etat: 'a_verifier', raison: null };
  if (!l.source) return { etat: 'a_verifier', raison: 'Aucune source documentaire choisie : la compatibilité reste à vérifier.' };
  if (!SANS_PROTOCOLE.includes(l.type)) {
    const manquants = nodes.filter((n) => (n.id === l.de || n.id === l.vers) && !declare(n.protocoles, l.type));
    if (manquants.length) {
      return { etat: 'a_verifier', raison: `${LIBELLE_LIAISON[l.type]} n’est pas déclaré sur ${manquants.map((n) => `« ${n.label} »`).join(' et ')} : compatibilité à vérifier.` };
    }
  }
  return { etat: 'etayee', raison: null };
}

export const nouvelId = (prefixe: string) => `${prefixe}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

// ─── Plans et cheminements ───
export type Point = [number, number];
export interface PlanVue {
  etageId: string | null;
  id: string; dossierId: string; nom: string; page: number; versionId: string;
  piece: { titre: string; fichier: string; mime: string; pages: number | null; version: string; statut: string } | null;
  versionRemplacee: boolean;
  echelle: {
    statut: 'aucune' | 'proposee' | 'validee'; mParUnite: string | null; points: [Point, Point] | null; distanceM: string | null;
    proposeePar: string | null; proposeeLe: string | null; valideePar: string | null; valideeLe: string | null;
  };
  nbCheminements: number; fichier: string;
}
export interface PieceDisponible { versionId: string; titre: string; fichier: string; mime: string; pages: number | null; version: string; statut: string }
export interface Longueurs {
  fiable: boolean; mention: string | null; unitesPlan: string; planeM: string | null; verticaleM: string | null; parCableM: string | null;
  totalM: string | null; reserveM: string | null; chutesM: string | null; aCommanderM: string | null;
}
export interface CheminementVue {
  nature: 'cable' | 'frigorifique'; refrigeration: import('../../../../shared/frigorifique.ts').Refrigeration | null;
  verticale: import('../../../../shared/frigorifique.ts').VerticalSpec | null; sourceVerticale: string; empreinte: string;
  majLe: string;
  id: string; planId: string; nom: string; typeCable: string | null; nbCables: number; points: Point[];
  reservePct: string; chutesPct: string; verticalesM: string | null; validation: { par: string | null; le: string } | null; longueurs: Longueurs;
}
export interface Quantites {
  tuyauteries: { cle: string; label: string; reference: string | null; quantity: string; unit: string; totalM?: string; reserveM?: string; chutesM?: string; fiable: boolean; toutValide: boolean; source: string; snapshot: any[] }[];
  charges: { fluide: string; complet: boolean; grammes: string | null; kg: string | null; manques: string[]; details: any[] }[];
  avertissementCharge: string;
  cablage: { cle: string; typeCable: string | null; nbCheminements: number; nbNonMesures: number; fiable: boolean; toutValide: boolean;
    totalM: string | null; reserveM: string | null; chutesM: string | null; aCommanderM: string | null; plans: string[] }[];
  toutFiable: boolean;
  plansNonValides: { id: string; name: string; statut: 'aucune' | 'proposee' }[];
  cheminementsNonValides: { id: string; nom: string; plan: string }[];
  nomenclature: { cle: string; type: TypeNoeud; typeLibelle: string; fabricant: string | null; reference: string | null; quantite: number; exemples: string[]; existant: boolean }[];
  envoiDevisDisponible: boolean;
}

export const TYPES_CABLE_SUGGERES = [
  'Câble bus KNX 2×2×0,8 mm', 'Câble Ethernet catégorie 6 U/FTP', 'Câble Ethernet catégorie 6A S/FTP', 'Câble RS-485 blindé 1 paire',
  'Câble DALI 2×1,5 mm²', 'Câble M-Bus 2×0,8 mm', 'Câble d’alimentation 3G1,5 mm²', 'Câble d’alimentation 3G2,5 mm²',
  'Câble de signal 2×0,75 mm² blindé', 'Fibre optique multimode OM3',
];

/** Lecture d'un nombre saisi à la française (« 12,5 ») ; null si invalide. */
export function lireNombre(v: string): number | null {
  const t = v.trim().replace(/\s/g, '').replace(',', '.');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}
